import { sumAllNumbersOfObject } from "../../../../util/dealingArrObj";

/* 수업 복사 검증
원본 수업의 값 중 "현재 학교 설정에 존재하는 것"만 남긴다.
학교마다, 학년도마다 lessonSetting/configMap이 다르므로 그대로 복사하면
저장이 막히거나(가중치 합계) 조용히 화면에서 사라지는(평가영역) 값이 생긴다.
계층(hierarchy)/학기(term_id)/교사(teachers)는 복사 대상이 아니므로 여기서 다루지 않는다. */

// 같은 학기에 복사하면 원본과 이름이 같아 구분이 안 되므로 접미사를 붙인다
const COPY_NAME_SUFFIX = "_copy";

const EVAL_METHOD = "evalMethod";
const EVAL_AREA_100 = "evalArea100";

// 평가제별로 의미가 있는 필드가 다르다
const WEIGHT_EVAL_TYPES = ["100점제", "커스텀"];
const SELECTED_AREA_EVAL_TYPE = "지정평가영역";
export const EVAL_ITEM_EVAL_TYPE = "평가항목별";

function findSetting(lessonSetting, itemCode) {
  return lessonSetting?.find((r) => r.item_code === itemCode);
}

function resolveEvaluationType(sourceType, lessonSetting, warnings) {
  const options = findSetting(lessonSetting, EVAL_METHOD)?.detailed_setting;

  if (!Array.isArray(options) || options.length === 0) return sourceType || "";
  if (options.includes(sourceType)) return sourceType;

  warnings.push(
    `평가제 "${sourceType}"은 현재 설정에 없어 "${options[0]}"으로 바꿨습니다.`,
  );
  return options[0];
}

function resolveEvalAreaWeights(
  sourceWeights,
  evaluationType,
  lessonSetting,
  warnings,
) {
  if (!WEIGHT_EVAL_TYPES.includes(evaluationType)) return null;
  if (!sourceWeights || typeof sourceWeights !== "object") return null;

  const areas = findSetting(lessonSetting, EVAL_AREA_100)?.detailed_setting;
  if (!Array.isArray(areas)) return null;

  const kept = {};
  const dropped = [];

  Object.entries(sourceWeights).forEach(([area, weight]) => {
    if (areas.includes(area)) kept[area] = weight;
    else dropped.push(area);
  });

  if (dropped.length > 0)
    warnings.push(
      `평가영역 가중치 [${dropped.join(
        ", ",
      )}]은 현재 설정에 없어 제외했습니다.`,
    );

  if (Object.keys(kept).length === 0) return null;

  // 100점제는 합계 100이 아니면 저장 자체가 막히므로 미리 알린다
  if (evaluationType === "100점제") {
    const sum = sumAllNumbersOfObject(kept);
    if (sum.toString() !== "100")
      warnings.push(
        `가중치 합계가 ${sum}입니다. 100이 되도록 수정한 뒤 저장하세요.`,
      );
  }

  return kept;
}

function resolveSelectedEvalAreas(sourceIds, lessonSetting, warnings) {
  if (!Array.isArray(sourceIds) || sourceIds.length === 0) return null;

  const activeIds = (
    findSetting(lessonSetting, EVAL_METHOD)?.extra_attributes?.evalItems || []
  )
    .filter((item) => item.active)
    .map((item) => item.id);

  const kept = sourceIds.filter((id) => activeIds.includes(id));
  const droppedCount = sourceIds.length - kept.length;

  if (droppedCount > 0)
    warnings.push(
      `평가영역 ${droppedCount}개가 현재 사용중이 아니어서 제외했습니다.`,
    );

  return kept.length > 0 ? kept : null;
}

function resolveExtraAttributes(
  sourceExtra,
  evaluationType,
  lessonSetting,
  allowedExtraKeys,
  warnings,
) {
  if (!sourceExtra || typeof sourceExtra !== "object") return null;

  const kept = {};
  const dropped = [];

  Object.entries(sourceExtra).forEach(([key, value]) => {
    if (key === "evalItems") return; // 아래에서 따로 검증한다
    if (value === null || value === undefined || value === "") return;
    if (allowedExtraKeys.includes(key)) kept[key] = value;
    else dropped.push(key);
  });

  if (dropped.length > 0)
    warnings.push(
      `[${dropped.join(
        ", ",
      )}] 항목은 현재 수업설정에서 사용하지 않아 제외했습니다.`,
    );

  if (evaluationType === SELECTED_AREA_EVAL_TYPE) {
    const selectedAreas = resolveSelectedEvalAreas(
      sourceExtra.evalItems,
      lessonSetting,
      warnings,
    );
    if (selectedAreas) kept.evalItems = selectedAreas;
  }

  return Object.keys(kept).length > 0 ? kept : null;
}

/**
 * 원본 수업에서 복사할 값과 경고 메시지를 만든다.
 * @param {object} sourceLesson 원본 수업 row
 * @param {Array} lessonSetting 현재 화면 기준 수업설정
 * @param {Array<string>} allowedExtraKeys 현재 화면에 실제로 렌더되는 extra_attributes 키
 * @returns {{ values: object, warnings: string[], willCopyEvalItems: boolean }}
 */
export function buildCopiedLessonValues({
  sourceLesson,
  lessonSetting,
  allowedExtraKeys = [],
}) {
  const warnings = [];

  const evaluationType = resolveEvaluationType(
    sourceLesson?.evaluation_type,
    lessonSetting,
    warnings,
  );

  const sourceName = sourceLesson?.subject_name || "";

  const values = {
    subject_name: sourceName ? `${sourceName}${COPY_NAME_SUFFIX}` : "",
    lesson_hours: sourceLesson?.lesson_hours || "",
    evaluation_type: evaluationType,
    eval_area_weights: resolveEvalAreaWeights(
      sourceLesson?.eval_area_weights,
      evaluationType,
      lessonSetting,
      warnings,
    ),
    extra_attributes: resolveExtraAttributes(
      sourceLesson?.extra_attributes,
      evaluationType,
      lessonSetting,
      allowedExtraKeys,
      warnings,
    ),
    tags: Array.isArray(sourceLesson?.tags) ? sourceLesson.tags : [],
  };

  return {
    values,
    warnings,
    willCopyEvalItems: evaluationType === EVAL_ITEM_EVAL_TYPE,
  };
}

/**
 * 현재 화면 설정에서 사용 중인 extra_attributes 키 목록을 만든다.
 * 수업생성 모달의 조건부 렌더링 조건과 같은 기준을 사용한다.
 */
export function getAllowedExtraKeys({
  configMap = {},
  hasAchievementOptions = false,
  flexibleDataValue = null,
}) {
  // 수업생성 모달과 동일한 조건. creditHour만 값이 있어야 렌더된다.
  const configKeys = [
    "subjectSort",
    "subjectCategory",
    "subjectCode",
    "subjectOrder",
    "subjectContent",
  ];

  const allowed = configKeys.filter((key) => configMap[key] !== "미사용");

  if (configMap.creditHour && configMap.creditHour !== "미사용")
    allowed.push("creditHour");

  if (hasAchievementOptions) allowed.push("achievement");
  if (flexibleDataValue?.secondSubjectContent)
    allowed.push("secondSubjectContent");
  if (flexibleDataValue?.specialSubjectContent)
    allowed.push("specialSubjectContent");

  return allowed;
}
